import { useEffect } from 'react'
import { Link } from 'react-router-dom'

export default function About() {
  useEffect(() => {
    document.title = 'Om mig - Kristensen Byg og Montage ApS'
    const metaDesc = document.querySelector('meta[name="description"]')
    if (metaDesc) {
      metaDesc.setAttribute(
        'content',
        'Læs historien bag Kristensen Byg og Montage ApS. Jeg er drevet af faglig stolthed, planlægning og et ønske om at levere tømrerarbejde af højeste kvalitet.'
      )
    }
  }, [])

  const getResizedUrl = (url, width, quality = 80) => {
    if (!url || !url.startsWith('https://assets.auxodev.dk/')) return url
    return url.replace(
      'https://assets.auxodev.dk/',
      `https://assets.auxodev.dk/cdn-cgi/image/width=${width},quality=${quality},format=auto/`
    )
  }

  const aboutImage = 'https://assets.auxodev.dk/assets/8ba53ab1-a105-4ab3-8373-4041e8d1e44e/about-planning-craftsman.jpg'

  return (
    <div className="bg-white min-h-screen">
      {/* Narrative Section */}
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Column: Content */}
          <div className="space-y-6">
            <h3 className="text-2xl sm:text-3xl font-bold uppercase tracking-wider text-anthracite">
              Skabt på baggrund af passion og solid erfaring
            </h3>
            <div className="space-y-6 text-sm sm:text-base text-warm-gray-600 leading-relaxed">
              <p>
                Kristensen Byg og Montage ApS er skabt ud fra en drøm om at levere tømrerarbejde, hvor der aldrig gås på kompromis med kvaliteten, og hvor jeg personligt kan stå 100% inde for hver eneste detalje.
              </p>
              <p>
                Med svendebrev fra 2011 og many års erfaring i byggebranchen løser jeg alt fra nybyggeri og renoveringer til komplekse specialopgaver med fokus på ordentlighed og holdbare løsninger.
              </p>
            </div>
            <div className="pt-2">
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 rounded bg-brand-green px-5 py-2.5 text-sm font-bold uppercase tracking-wider text-white hover:bg-brand-green-hover transition-colors duration-200"
              >
                Kontakt mig
                <i className="fa-light fa-arrow-right" />
              </Link>
            </div>
          </div>

          {/* Right Column: Image */}
          <div className="relative aspect-4/3 overflow-hidden border border-warm-gray-200 shadow-md">
            <img
              src={getResizedUrl(aboutImage, 800, 80)}
              alt="Tømrermester Kenneth Kristensen planlægger et byggeprojekt"
              className="h-full w-full object-cover"
            />
          </div>
        </div>
      </div>

      {/* Values Section */}
      <div className="bg-warm-gray-50 border-t border-b border-warm-gray-200 py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <p className="text-2xl sm:text-3xl font-bold uppercase tracking-wider text-anthracite">
              Det fundament jeg bygger på
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Value 1 */}
            <div className="bg-white p-8 rounded-lg border border-warm-gray-200 shadow-sm text-center flex flex-col items-center">
              <div className="text-brand-green text-3xl mb-6">
                <i className="fa-light fa-hammer" />
              </div>
              <h4 className="text-lg font-bold uppercase tracking-wider text-anthracite mb-3">Faglig stolthed</h4>
              <p className="text-sm text-warm-gray-600 leading-relaxed">
                Jeg afleverer kun arbejde, som jeg selv kan være fuldt ud stolt af.
              </p>
            </div>

            {/* Value 2 */}
            <div className="bg-white p-8 rounded-lg border border-warm-gray-200 shadow-sm text-center flex flex-col items-center">
              <div className="text-brand-green text-3xl mb-6">
                <i className="fa-light fa-calendar-lines-pen" />
              </div>
              <h4 className="text-lg font-bold uppercase tracking-wider text-anthracite mb-3">Grundig planlægning</h4>
              <p className="text-sm text-warm-gray-600 leading-relaxed">
                Et godt resultat sikres gennem gennemtænkte processer fra start til slut.
              </p>
            </div>

            {/* Value 3 */}
            <div className="bg-white p-8 rounded-lg border border-warm-gray-200 shadow-sm text-center flex flex-col items-center">
              <div className="text-brand-green text-3xl mb-6">
                <i className="fa-light fa-comments" />
              </div>
              <h4 className="text-lg font-bold uppercase tracking-wider text-anthracite mb-3">Tæt dialog</h4>
              <p className="text-sm text-warm-gray-600 leading-relaxed">
                Det bedste samarbejde skabes gennem åbenhed og løbende forventningsafstemning.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="relative bg-white py-16 md:py-24 text-center overflow-hidden border-t border-warm-gray-100">
        {/* Slanted red section on the right */}
        <div className="absolute top-0 right-0 h-full w-1/4 bg-brand-green transform -skew-x-12 translate-x-1/4 hidden md:block pointer-events-none" />
        <div className="absolute top-0 right-0 h-full w-1/4 bg-brand-green-light/20 transform -skew-x-12 translate-x-1/3 hidden md:block pointer-events-none" />
        
        <div className="relative z-10 mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <h3 className="text-2xl sm:text-3xl font-bold uppercase tracking-wider text-anthracite mb-6">
            Skal jeg hjælpe dig med dit næste projekt?
          </h3>
          <p className="text-sm sm:text-base text-warm-gray-600 leading-relaxed max-w-2xl mx-auto mb-10">
            Uanset om du står overfor en større renovering, et nybyggeri eller en montageopgave, står jeg klar med professionel rådgivning og solidt håndværk.
          </p>
          <Link
            to="/kontakt"
            className="inline-flex items-center justify-center rounded bg-brand-green px-6 py-3.5 text-sm font-bold text-white shadow-sm hover:bg-brand-green-hover transition-colors duration-200"
          >
            hør mere om dit byggeprojekt
          </Link>
        </div>
      </div>
    </div>
  )
}
